// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { useNavigate } from "react-router-dom";
import { useFacility } from "@/contexts/FacilityContext";
import { Building2, ChevronDown, Plus, Settings, QrCode } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

export function FacilitySwitcher() {
  const { activeFacility, facilities, switchFacility, loading, isAdmin } = useFacility();
  const navigate = useNavigate();

  if (loading || facilities.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 text-xs font-medium max-w-[180px]">
          <Building2 className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{activeFacility?.name || "Select Facility"}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {facilities.map(f => (
          <DropdownMenuItem
            key={f.id}
            onClick={() => switchFacility(f.id)}
            className={f.id === activeFacility?.id ? "bg-accent" : ""}
          >
            <Building2 className="h-4 w-4 mr-2" />
            <span className="truncate">{f.name}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        {activeFacility && isAdmin && (
          <DropdownMenuItem onClick={() => navigate(`/facility/${activeFacility.id}/settings`)}>
            <Settings className="h-4 w-4 mr-2" />
            Facility Settings
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate("/facility/create")}>
          <Plus className="h-4 w-4 mr-2" />
          Create Facility
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
